import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Card, CardContent, Typography, Button, Box } from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Alert } from '@material-ui/lab';
import { useDispatch } from 'react-redux';
import { elementUpdated } from '../features/workflow/workflowSlice';

const useStyles = makeStyles((theme) => ({
  errorCard: {
    margin: theme.spacing(1),
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: '8px',
    maxWidth: '400px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  errorContent: {
    padding: theme.spacing(2),
  },
  errorIcon: {
    fontSize: 20,
    marginRight: theme.spacing(1),
    color: '#856404',
  },
  errorTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#856404',
    marginBottom: theme.spacing(1),
  },
  errorMessage: {
    fontSize: '12px',
    color: '#856404',
    marginBottom: theme.spacing(1),
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  retryButton: {
    fontSize: '12px',
    padding: '4px 8px',
    minWidth: 'auto',
    backgroundColor: '#856404',
    color: 'white',
    '&:hover': {
      backgroundColor: '#6c5203',
    },
  },
  resetButton: {
    fontSize: '12px',
    padding: '4px 8px',
    minWidth: 'auto',
    borderColor: '#856404',
    color: '#856404',
    '&:hover': {
      borderColor: '#6c5203',
      backgroundColor: 'rgba(133, 100, 4, 0.1)',
    },
  },
  warningAlert: {
    marginTop: theme.spacing(1),
    fontSize: '11px',
  },
}));

class WorkflowErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Log error details
    if (process.env.NODE_ENV === 'development') {
      console.error('WorkflowErrorBoundary caught an error:', error, errorInfo);
    }

    // Send error to monitoring service
    this.logWorkflowError(error, errorInfo);
  }

  logWorkflowError = (error, errorInfo) => {
    try {
      // Send workflow-specific error data
      const errorData = {
        error: error.toString(),
        errorInfo: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        workflowId: this.props.workflowId,
        nodeId: this.props.nodeId,
        component: this.props.componentName || 'Unknown',
        retryCount: this.state.retryCount,
      };

      fetch('/api/errors/workflow', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorData),
      }).catch(() => {
        // Silently fail if error reporting fails
      });
    } catch (e) {
      // Silently fail if error reporting fails
    }
  };

  handleRetry = () => {
    const newRetryCount = this.state.retryCount + 1;
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: newRetryCount 
    });
  };

  handleResetNode = () => {
    if (this.props.nodeId && this.props.dispatch) {
      // Reset the node to a default state
      this.props.dispatch(elementUpdated(this.props.nodeId, {
        data: {
          ...this.props.nodeId,
          error: null,
          testData: null,
        }
      }));
    }
    this.handleRetry();
  };

  render() {
    if (this.state.hasError) {
      return (
        <WorkflowErrorFallback 
          onRetry={this.handleRetry}
          onResetNode={this.handleResetNode}
          error={this.state.error}
          nodeId={this.props.nodeId}
          retryCount={this.state.retryCount}
        />
      );
    }

    return this.props.children;
  }
}

const WorkflowErrorFallback = ({ onRetry, onResetNode, error, nodeId, retryCount }) => {
  const classes = useStyles();

  return (
    <Card className={classes.errorCard}>
      <CardContent className={classes.errorContent}>
        <Box display="flex" alignItems="center" mb={1}>
          <ErrorOutlineIcon className={classes.errorIcon} />
          <Typography className={classes.errorTitle}>
            Workflow Error
          </Typography>
        </Box>
        
        <Typography className={classes.errorMessage}>
          There was an error processing this workflow step. This might be due to invalid data or a temporary issue.
        </Typography>
        
        {process.env.NODE_ENV === 'development' && error && (
          <Typography variant="caption" style={{ color: '#856404', fontSize: '10px' }}>
            Error: {error.toString()}
          </Typography>
        )}
        
        {retryCount > 0 && (
          <Typography variant="caption" style={{ color: '#856404', fontSize: '10px', display: 'block', marginTop: '4px' }}>
            Retry attempt #{retryCount}
          </Typography>
        )}

        <Box className={classes.buttonContainer}>
          <Button
            variant="contained"
            className={classes.retryButton}
            startIcon={<RefreshIcon style={{ fontSize: '14px' }} />}
            onClick={onRetry}
          >
            Try Again
          </Button>
          
          {nodeId && (
            <Button
              variant="outlined"
              className={classes.resetButton}
              onClick={onResetNode}
            >
              Reset Node
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// HOC to wrap components with error boundary
export const withWorkflowErrorBoundary = (Component, componentName) => {
  return (props) => {
    const dispatch = useDispatch();
    
    return (
      <WorkflowErrorBoundary 
        componentName={componentName}
        workflowId={props.workflowId}
        nodeId={props.nodeId}
        dispatch={dispatch}
      >
        <Component {...props} />
      </WorkflowErrorBoundary>
    );
  };
};

export default WorkflowErrorBoundary;
